import { Box, Card, IconButton, Paragraph } from '@zillow/constellation-10';
import { IconCloseCircleOutline } from '@zillow/constellation-icons';
import AttachmentIcon from '../AttachmentIcon';
import { cardStyles, contentStyles } from './styles';
import { FILE_TYPES } from '../../../constants';
import ImageThumbnail from '../ImageThumbnail';

export type UploadedAttachmentCardProps = {
  fileName: string;
  fileType: string;
  imageSrc: string;
  onClick(zDocId: string): void;
  onRemove(zDocId: string): void;
  zDocId: string;
};

type RemoveAttachmentButtonProps = {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const RemoveAttachmentButton = ({ onClick }: RemoveAttachmentButtonProps) => {
  return (
    <IconButton
      data-testid="remove-attachment-button"
      shape="circle"
      emphasis="filled"
      className="remove-attachment-button"
      icon={<IconCloseCircleOutline />}
      onClick={onClick}
      size="md"
      title="Remove attachment"
      css={{
        height: 'auto',
        width: 'auto',
        backgroundColor: 'bg.neutral',
        position: 'absolute',
        top: '-5px',
        right: '-5px',
      }}
    />
  );
};

export default function UploadedAttachmentCard({
  fileName,
  fileType,
  imageSrc,
  onClick,
  onRemove,
  zDocId,
}: UploadedAttachmentCardProps) {
  const isImageFileType = fileType?.includes(FILE_TYPES.IMAGE) ?? false;
  const isVideoFileType = fileType?.includes(FILE_TYPES.VIDEO) ?? false;

  const handleRemoveAttachment = (event: React.MouseEvent<HTMLButtonElement>) => {
    // NOTE: We are not able to delete files on the FE via the zDocs SDK at this time.
    event.stopPropagation();
    return onRemove(zDocId);
  };

  const handleViewImageAttachment = () => {
    return onClick(zDocId);
  };

  if (isImageFileType) {
    return (
      <Box css={{ position: 'relative', maxWidth: 'fit-content', alignSelf: 'flex-start', marginTop: 'tight' }}>
        <ImageThumbnail
          fileName={fileName}
          isImpersonator={false}
          onClick={handleViewImageAttachment}
          shouldDarkenImage={false}
          shouldShowExpiredCaption={false}
          shouldShowPlaceholder={!imageSrc}
          size={72}
          url={imageSrc}
        />

        <RemoveAttachmentButton onClick={handleRemoveAttachment} />
      </Box>
    );
  }

  return (
    <Box css={{ position: 'relative', maxWidth: 'fit-content', alignSelf: 'flex-start', marginTop: 'tight' }}>
      <Card data-testid="uploaded-attachment-preview-card" interactive={false} elevated={false} css={cardStyles}>
        <AttachmentIcon isImageFileType={false} isVideoFileType={isVideoFileType} isDisabled={false} />

        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            marginLeft: 'default',
            minWidth: 0,
            flex: 1,
          }}
        >
          <Paragraph textStyle="body-sm" css={contentStyles}>
            {fileName}
          </Paragraph>
          <Paragraph textStyle="body-sm" css={{ color: 'text.subtle', ...contentStyles }}>
            {fileType}
          </Paragraph>
        </Box>
      </Card>

      <RemoveAttachmentButton onClick={handleRemoveAttachment} />
    </Box>
  );
}
